import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography, Grid, Paper, CircularProgress, Chip } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { LOGOUT } from '../graphql/logout';
import { GET_ME } from '../graphql/getMe';
import { useAuthenticated } from '../hooks/useAuthenticated';

const Pricing = () => {
  const navigate = useNavigate();
  const { isPlanActive, loading: authLoading } = useAuthenticated();
  const { data: me } = useQuery(GET_ME);
  const [loading, setLoading] = useState<string | null>(null);
  const [logout] = useMutation(LOGOUT, {
    update: (cache) => {
      cache.evict({ fieldName: 'getMe' });
      cache.gc();
    },
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handlePurchase = async (planId: string) => {
    setLoading(planId);
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/checkout_sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ planId }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to initiate purchase. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const plans = [
    // Show 2-day trial plan only if planExpiresAt is null
    ...(me?.getMe?.planExpiresAt === null ? [{
      id: '2-day-trial',
      title: '2-Days Full Access',
      price: '$1.00',
      period: '2-day access',
      description: 'A gentle spark to activate your journey—explore the path to mastery for just $1.',
      recommended: false,
    }] : []),
    {
      id: 'qi-tonic',
      title: 'Qi Tonic',
      price: '$59.99',
      period: '1 month access',
      description: 'A quick infusion of focus and energy to power through your prep.',
      recommended: false,
    },
    {
      id: 'vital-path',
      title: 'Vital Path',
      price: '$79.99',
      period: '6 months access',
      description: 'Stay aligned and energized for the long haul.',
      recommended: true,
    },
    {
      id: 'inner-mastery',
      title: 'Inner Mastery',
      price: '$129.99',
      period: '1 year access',
      description: 'Reach the highest level of balance, wisdom, and readiness.',
      recommended: false,
    },
  ];

  useEffect(() => {
    if (!isPlanActive) {
      navigate('/pricing');
    } else {
      navigate('/dashboard');
    }
  }, [isPlanActive, navigate]);

  return (
    <Box
      sx={{
        minHeight: '111.11vh',
        background: '#231f1e',
        position: 'relative',
      }}
    >
      {/* Logout Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 40,
          zIndex: 1,
          display: 'flex',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={handleLogout}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            px: 3,
            py: 1,
            fontSize: '1rem',
            color: '#fff',
            borderColor: '#fff',
            '&:hover': {
              background: '#fff',
              color: '#231f1e',
              borderColor: '#fff',
            },
          }}
        >
          Logout
        </Button>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl">
        <Box
          sx={{
            pt: { xs: 15, md: 20 },
            pb: { xs: 8, md: 12 },
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#b3c6e7',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 2,
              fontFamily: '"Higuen Serif", serif',
            }}
          >
            {me?.getMe?.planExpiresAt && !isPlanActive ? 'Upgrade Your Plan' : 'Choose Your Plan'}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 6,
              color: '#e0d7ce',
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
            }}
          >
            {me?.getMe?.planExpiresAt && !isPlanActive 
              ? 'You have no active plan. Upgrade to a new plan to continue your board exam preparation'
              : 'Select the perfect plan to accelerate your board exam preparation'
            }
          </Typography>

          <Grid container spacing={3} sx={{ mt: 4 }}>
            {plans.map((plan, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={plans.length === 3 ? 4 : (index === 0 ? 12 : 4)} 
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Paper
                  elevation={plan.recommended ? 8 : 3}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: '20px',
                    transition: 'transform 0.2s',
                    border: plan.recommended ? '3px solid #ffa726' : 'none',
                    position: 'relative',
                    background: '#181412',
                    color: '#fff',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                    width: index === 0 && plans.length === 4? '60%' : '100%',
                  }}
                >
                  {plan.recommended && (
                    <Chip
                      label="RECOMMENDED"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#ffa726',
                        color: '#231f1e',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        letterSpacing: 1,
                      }}
                    />
                  )}
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{ 
                      mb: 2, 
                      fontWeight: 600,
                      color: '#fff',
                      fontFamily: '"Higuen Serif", serif'
                    }}
                  >
                    {plan.title}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ 
                      mb: 1, 
                      fontWeight: 400, 
                      color: plan.recommended ? '#ffa726' : '#6ec6ff',
                      fontSize: plan.recommended ? '2.5rem' : '2rem',
                      textShadow: '0 1px 8px #181412',
                    }}
                  >
                    {plan.price}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ 
                      mb: 3, 
                      color: '#e0d7ce',
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      textTransform: 'uppercase',
                      letterSpacing: 1
                    }}
                  >
                    {plan.period}
                  </Typography>
                  <Typography sx={{ mb: 4, color: '#e0d7ce' }}>
                    {plan.description}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                      borderRadius: '30px',
                      py: 1.5,
                      fontSize: '1.1rem',
                      textTransform: 'none',
                      backgroundColor: plan.recommended ? '#ffa726' : '#1976d2',
                      color: plan.recommended ? '#231f1e' : '#fff',
                      fontWeight: plan.recommended ? 600 : undefined,
                      '&:hover': {
                        backgroundColor: plan.recommended ? '#fb8c00' : '#1565c0',
                        color: '#fff',
                      }
                    }}
                    onClick={() => handlePurchase(plan.id)}
                  >
                    {loading === plan.id ? <CircularProgress size={24} /> : 'Purchase'}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;
